import {GatewayType} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {ValidationInfo} from 'schemas/tags/EditableSchemaTag';

export const EMPOWER_ADAPTER_ID = 'empower-adapter';

export interface InstrumentAdapter {
  id: string;
  displayName: string;
  gatewayType?: GatewayType;
}

export interface AdapterField {
  id: string;
  displayName: string;
  info: string | null;
  isRequired: boolean;
  isUneditable?: boolean;
  defaultValue?: string;
  fieldType?: string;
}

export interface AdapterCommand {
  // The command's name, e.g. VALIDATE_CREDENTIALS.
  id: string;
}

export const VALIDATE_CREDENTIALS_COMMAND = 'VALIDATE_CREDENTIALS';

export enum ConnectionStatus {
  PENDING = 'PENDING',
  AVAILABLE = 'AVAILABLE',
  RUNNING = 'RUNNING',
  INSTRUMENT_ERROR = 'INSTRUMENT_ERROR',
  COMMUNICATION_ERROR = 'COMMUNICATION_ERROR',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  DISABLED = 'DISABLED',
}

export enum ConnectionFieldType {
  TEXT = 'TEXT',
  SECRET_REF = 'SECRET_REF',
  PARAMETER = 'PARAMETER',
  GATEWAY = 'GATEWAY',
}

export interface ConnectionStatusResponse {
  value: ConnectionStatus;
  timestamp: string;
}

export interface InstrumentServiceDetails {
  id: string;
  adapterId: string;
  adapterDisplayName?: string;
  gatewayId: string;
  instrumentName: string;
  status: ConnectionStatusResponse;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  fields: {[id: string]: any};
  fieldValidationInfoByName: {[id: string]: ValidationInfo};
  allotropyVendor?: string;
  isFileSyncing?: boolean;
  isFileBrowsing?: boolean;
  filesToSync?: number;
}

export enum InstrumentServiceLoadStatus {
  UNSET = 'UNSET',
  LOADING = 'LOADING',
  // This is the only status that should have data
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE',
}

export interface InstrumentServiceDetailsLoader {
  status: InstrumentServiceLoadStatus;
  data: InstrumentServiceDetails | null;
}

/**
 * Determines if a connection requires the user to log in before sending commands.
 *
 * Login is required when the adapter authenticates per user and the connection does
 * not already store shared credentials:
 * - Empower always authenticates a user, so login is required whenever no shared
 *   username/password is configured on the connection.
 * - Other adapters (e.g. Chromeleon in "User Mode") signal per-user login via the
 *   per_user_login_required field; login is required when that field is 'true' and no
 *   shared credentials are configured.
 *
 * If shared credentials are already configured on the connection, no login prompt is
 * needed in either case.
 */
export function connectionRequiresUserLogin(
  instrumentServiceDetails: InstrumentServiceDetails | null | undefined
): boolean {
  if (!instrumentServiceDetails) {
    return false;
  }

  const hasCredentials = connectionHasSharedCredentials(instrumentServiceDetails);

  // Empower always authenticates a user; there is no per-connection toggle for it.
  if (instrumentServiceDetails.adapterId === EMPOWER_ADAPTER_ID) {
    return !hasCredentials;
  }

  const perUserLoginRequired = instrumentServiceDetails.fields.per_user_login_required?.value === 'true';
  return perUserLoginRequired && !hasCredentials;
}

/**
 * Whether the login prompt checks credentials with VALIDATE_CREDENTIALS, which only logs in, rather
 * than BROWSE_FILES. Empower doesn't browse files, so its connections can only be validated this way.
 */
export function connectionValidatesLoginWithValidateCredentials(
  instrumentServiceDetails: InstrumentServiceDetails | null | undefined
): boolean {
  return instrumentServiceDetails?.adapterId === EMPOWER_ADAPTER_ID;
}

/**
 * Returns true if the connection stores a non-empty shared username and password.
 * Treats null, undefined, empty string, and whitespace-only values as unset.
 */
function connectionHasSharedCredentials(instrumentServiceDetails: InstrumentServiceDetails): boolean {
  const username = instrumentServiceDetails.fields.username?.value?.trim();
  const password = instrumentServiceDetails.fields.password?.value?.trim();
  return Boolean(username) && Boolean(password);
}

/**
 * Check if a string value is empty or contains only whitespace.
 * This does NOT trim the value - it only checks if it's all whitespace.
 */
function isEmptyOrWhitespace(value: string | null | undefined): boolean {
  return !value || value.trim() === '';
}

/**
 * Check if the connection has valid credentials for auto-sync.
 * Returns true if per-user login is not required, or if both username and password are set.
 */
export function connectionHasCredentials(
  instrumentServiceDetails: InstrumentServiceDetails | null | undefined
): boolean {
  if (!instrumentServiceDetails) {
    return false;
  }
  if (instrumentServiceDetails.fields?.per_user_login_required?.value !== 'true') {
    return true;
  }
  const username = instrumentServiceDetails.fields?.username?.value;
  const password = instrumentServiceDetails.fields?.password?.value;
  return !isEmptyOrWhitespace(username) && !isEmptyOrWhitespace(password);
}
